import React from "react";
import { Table } from "../components/table/Table";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {Button, Checkbox, Stack} from "@mui/material";
import { DefaultError } from "@tanstack/query-core";
import { enqueueSnackbar } from "notistack";
import { TableHeaderProps } from "../components/table/TableTypes";
import { QuickLogin } from "../types/QuickLogin";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { copyToClipboard } from "../utils/Clipboard";
import { deleteQuickLogins, loadQuickLogins, QuickLoginFilter, updateQuickLogins } from "../service/quick.logins";
import { Delete } from "../components/common/Delete";
import dayjs from "dayjs";
import {TextInput} from "../components/form/TextInput";
import {Highlighter} from "../components/common/Highlighter";
import {Link} from "react-router";
import {FiltersCluster} from "../components/form/FilterCluster";


export function QuickLoginPage() {
    const queryClient = useQueryClient()

    const deleteMutation = useMutation<void, DefaultError, string>({
        mutationFn: (id) => deleteQuickLogins(id).then(() => void 0),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["quick-logins"] }).catch(e => {
                enqueueSnackbar({ message: "Failed to clean cache after delete user: " + e.message, variant: 'error' })
            });
        },
        onError: (error) => {
            enqueueSnackbar({ message: "Failed to delete user: " + error.message, variant: 'error' })
        }
    });
    const updateNotifiedutation = useMutation<void, DefaultError, QuickLogin>({
        mutationFn: (payload) => updateQuickLogins(payload.id, payload).then(() => void 0),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["quick-logins"] }).catch(e => {
                enqueueSnackbar({ message: "Failed to clean cache after delete user: " + e.message, variant: 'error' })
            });
        },
        onError: (error) => {
            enqueueSnackbar({ message: "Failed to delete user: " + error.message, variant: 'error' })
        }
    });

    const [filter, setFilter] = React.useState<QuickLoginFilter>({
        page: 0,
        size: 25,
        phone: '',
        sortKey: 'notified',
        sortOrder: 'asc',
    });

    const { data, isFetching } = useQuery({
        queryKey: ['quick-logins', filter],
        refetchOnWindowFocus: false,
        queryFn: () => {
            return loadQuickLogins(fixFilter(filter)).then(response => response.data)
        },
        placeholderData: keepPreviousData
    });

    function fixFilter(filter:QuickLoginFilter):QuickLoginFilter {
        const newFilter = {...filter};
        //@ts-ignore
        newFilter.sort = filter.sortKey + "," + filter.sortOrder;
        //@ts-ignore
        delete newFilter.sortKey;
        //@ts-ignore
        delete newFilter.sortOrder
        if(newFilter.phone) {
            newFilter.phone = '%' + newFilter.phone + '%';
        }
        return newFilter;
    }

    const texts = {
        ru: "Коллега, мы создали удобный ресурс для подбора квартир. Туда попали ваши объявления с других сайтов." +
            " Пожалуйста, войдите в ваш личный кабинет по ссылке, отметьте актуальные" +
            " реальные объявления зеленым (измените статус), и проверьте вся ли информация верна. Также вы можете размещать новые объявления.",

        en: "Colleague, we have created a convenient resource for finding apartments. Your listings from other websites have been added there." +
            " Please log in to your personal account via the link, mark the active real listings in green (change status)," +
            " and check that all the information is correct. You can also post new property listings.",

        ka: "კოლეგავ, ჩვენ შევქმენით მოსახერხებელი რესურსი ბინების შესარჩევად. იქ მოხვდა თქვენი განცხადებები სხვა საიტებიდან." +
            " გთხოვთ, შედით თქვენს პირად კაბინეტში ბმულის საშუალებით, მონიშნეთ აქტუალური" +
            " რეალური განცხადებები მწვანედ და გადაამოწმეთ, ინფორმაცია სწორია თუ არა. ასევე შეგიძლიათ განათავსოთ ახალი უძრავი ქონების განცხადებები."
    }

    function copy(text: string, quickLogin: QuickLogin) {
        copyToClipboard(text + " " + generateLink(quickLogin))
    }

    function generateLink(row: QuickLogin) {
        return document.location.protocol + "//" + document.location.host + "/profile?login&ql=" + row.id + (row?.user?.phone ? "&phone=" + row.user.phone +"&tab=my" : "");
    }

    const headers: TableHeaderProps<QuickLogin>[] = [
        {
            key: "createdAt",
            title: "Created At",
            render: (row) => row.createdAt ? dayjs(row.createdAt).format('YYYY-MM-DD HH:mm') : <span/>
        },
        {
            key: "updatedAt",
            title: "Updated At",
            render: (row) => row.updatedAt ? dayjs(row.updatedAt).format('YYYY-MM-DD HH:mm') : <span/>
        },
        {
            key: "phone",
            title: "Phone",
            sort: "user.phone",
            render: (row) => row.user?.phone ? <Button component={Link}
                        to={"https://wa.me/" + row.user?.phone + "?text=" + encodeURIComponent(texts.en + " " + generateLink(row))}
                        target={"_blank"}>
                <Highlighter text={row.user?.phone} search={filter.phone} />
            </Button> : "[no phone]",
        },
        {
            key: "text_ru",
            disableSort: true,
            title: "Text",
            render: (row) => <Stack flexDirection={"column"}>
                <Stack alignItems={"center"}>
                    <Button onClick={() => copy(texts.en + "\n\n" + texts.ka + "\n\n" + texts.ru, row)} endIcon={<ContentCopyIcon/>}>Copy</Button>
                </Stack>
                <div>{texts.en} {generateLink(row)}</div>
            </Stack>
        },
        {
            key: "notified",
            title: 'Notified Mark',
            render: (row) => <Stack flexDirection={"row"}>
                <Checkbox checked={row.notified} onChange={() => updateNotifiedutation.mutate({...row, notified: !row.notified})} />
                <Delete onConfirm={() => deleteMutation.mutate(row.id)}/>
            </Stack>
        }
    ]


    return <>
        <FiltersCluster className={"mb-15"}>
            <TextInput fullWidth label={"Phone"} value={filter.phone} onChange={e => {
                setFilter({ ...filter, page: 0, phone: e.target.value });
            }}/>
        </FiltersCluster>
        <Table data={data?.content || []}
               sort={{
                   key: filter.sortKey,
                   order: filter.sortOrder,
                   onChange: (key, order) => setFilter({ ...filter, page: 0, sortKey: key, sortOrder: order })
               }}
               isLoading={isFetching}
               total={data?.totalElements || 0}
               page={filter.page}
               size={filter.size}
               headers={headers}
               pageChange={(page, size) => setFilter({ ...filter, page, size })}/>
    </>
};
